import { AnimatePresence, motion } from 'framer-motion';
import { Hammer, Heart, Users, Waypoints } from 'lucide-react';
import { useMemo } from 'react';
import type { Building, Citizen } from '../types/core';
import { useGameStore } from '../simulation/store';
import { getBuildingDefinition } from '../world/buildingDefinitions';

function namesFor(ids: readonly string[], citizens: readonly Citizen[]): string {
  const names = ids
    .map((id) => citizens.find((citizen) => citizen.id === id)?.name)
    .filter((name): name is string => Boolean(name))
    .slice(0, 4);

  if (names.length === 0) {
    return 'None';
  }

  return names.join(', ');
}

function BuildingPanel({ building, citizens }: { readonly building: Building; readonly citizens: readonly Citizen[] }) {
  const definition = getBuildingDefinition(building.kind);
  const residentNames = namesFor(building.residents, citizens);
  const workerNames = namesFor(building.workers, citizens);
  const visitorNames = namesFor(building.visitors, citizens);

  return (
    <>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-hive-ink/55">{definition.label}</p>
        <h2 className="font-display text-lg font-bold">{building.name}</h2>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-md bg-white/55 p-2">
          <div className="flex items-center gap-1 text-xs font-bold uppercase text-hive-ink/55">
            <Hammer size={13} /> Stage
          </div>
          <div className="font-semibold capitalize">{building.stage.replace('-', ' ')}</div>
        </div>
        <div className="rounded-md bg-white/55 p-2">
          <div className="flex items-center gap-1 text-xs font-bold uppercase text-hive-ink/55">
            <Heart size={13} /> Mood
          </div>
          <div className="font-semibold">{building.mood}</div>
        </div>
        <div className="rounded-md bg-white/55 p-2">
          <div className="flex items-center gap-1 text-xs font-bold uppercase text-hive-ink/55">
            <Users size={13} /> People
          </div>
          <div className="font-semibold">
            {building.zone === 'residential'
              ? `${building.residents.length}/${definition.capacity}`
              : `${building.workers.length}/${definition.jobs}`}
          </div>
        </div>
        <div className="rounded-md bg-white/55 p-2">
          <div className="flex items-center gap-1 text-xs font-bold uppercase text-hive-ink/55">
            <Waypoints size={13} /> Status
          </div>
          <div className="font-semibold">{building.operatingStatus}</div>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        {building.zone === 'residential' ? (
          <p>
            <span className="font-bold">Residents:</span> {residentNames}
          </p>
        ) : null}
        {building.zone !== 'residential' ? (
          <>
            <p>
              <span className="font-bold">Employees:</span> {workerNames}
            </p>
            <p>
              <span className="font-bold">Visitors:</span> {visitorNames}
            </p>
            <p>
              <span className="font-bold">Popularity:</span> {building.popularity}
            </p>
          </>
        ) : null}
        {building.zone === 'workspace' ? (
          <p>
            <span className="font-bold">Production:</span> {building.production}
          </p>
        ) : null}
      </div>
    </>
  );
}

function CitizenPanel({ citizen }: { readonly citizen: Citizen }) {
  return (
    <>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-hive-ink/55">Citizen</p>
        <h2 className="font-display text-lg font-bold">{citizen.name}</h2>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-md bg-white/55 p-2">
          <div className="text-xs font-bold uppercase text-hive-ink/55">Age</div>
          <div className="font-semibold">{citizen.age}</div>
        </div>
        <div className="rounded-md bg-white/55 p-2">
          <div className="text-xs font-bold uppercase text-hive-ink/55">Mood</div>
          <div className="font-semibold">{Math.round(citizen.happiness)}%</div>
        </div>
      </div>
      <p className="text-sm">
        <span className="font-bold">Current Activity:</span> {citizen.activity}
      </p>
    </>
  );
}

export function Inspector() {
  const hover = useGameStore((state) => state.hover);
  const selectedBuildingId = useGameStore((state) => state.selectedBuildingId);
  const buildings = useGameStore((state) => state.world.buildings);
  const citizens = useGameStore((state) => state.world.citizens);
  const building = useMemo(
    () => buildings.find((item) => item.id === (selectedBuildingId ?? hover.buildingId)),
    [buildings, hover.buildingId, selectedBuildingId]
  );
  const citizen = useMemo(() => citizens.find((item) => item.id === hover.citizenId), [citizens, hover.citizenId]);
  const visible = Boolean(building || citizen);

  return (
    <AnimatePresence>
      {visible ? (
        <motion.section
          className="glass-panel pointer-events-auto absolute right-3 top-24 z-30 w-[min(21rem,calc(100%-1.5rem))] rounded-lg p-4 md:right-4"
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
        >
          <div className="space-y-3">
            {building ? <BuildingPanel building={building} citizens={citizens} /> : null}
            {!building && citizen ? <CitizenPanel citizen={citizen} /> : null}
          </div>
        </motion.section>
      ) : null}
    </AnimatePresence>
  );
}
